import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Pagination,
  PaginationItem,
  PaginationLink
} from 'reactstrap';
import Select from 'react-select';
import { fetchQuestions } from 'redux/QuestionBank/QuestionsAction';
import { fetchSubjects, fetchUnits, fetchTopics } from 'redux/QuestionBank/SubjectsUnitsAction';
import { fetchPassages } from 'redux/QuestionBank/PassageAction';
import { draftRawToHtml } from 'util/draftToHtml';
import { formatDate } from 'util/formatDate';
import { customDropdownWithBorder } from 'util/reactCustomStyles';
import { getDisplayName } from 'util/specialtyDisplayHelper';
import Header from 'components/Headers/Header';
import AccessRestriction from 'components/AccessRestriction/AccessRestriction';
import TrialBanner from 'components/Trial/TrialBanner';
import BoardsCountdownBanner from 'components/Trial/BoardsCountdownBanner';
import AddonCrossSellBanner from 'components/Billing/AddonCrossSellBanner';

const SearchQuestions = () => {
  const dispatch = useDispatch();

  const { questions, loading: questionsLoading } = useSelector(state => state.questionBankQuestions);
  const { subjects, loading: subjectsLoading } = useSelector(state => state.questionBankSubjects);
  const { units, loading: unitsLoading } = useSelector(state => state.questionBankUnits);
  const { passages } = useSelector(state => state.questionBankPassages);
  const { topics, loading: topicsLoading } = useSelector(state => state.questionBankTopics);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  const isInitialLoading = questionsLoading || subjectsLoading || unitsLoading || topicsLoading;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterSubject, setFilterSubject] = useState(null);
  const [filterUnit, setFilterUnit] = useState(null);
  const [filterTopic, setFilterTopic] = useState(null);
  const [filterDifficulty, setFilterDifficulty] = useState(null);
  const [filterQuality, setFilterQuality] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 25;

  useEffect(() => {
    dispatch(fetchQuestions());
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchPassages());
  }, [dispatch, activeSpecialty]);

  // Convert options for react-select (with code prefix)
  const subjectOptions = subjects.map(s => ({
    value: s.subjectId,
    label: getDisplayName(s, activeSpecialty)
  }));
  const unitOptions = units
    .filter(u => !filterSubject || u.subjectId === filterSubject)
    .map(u => ({
      value: u.unitId,
      label: getDisplayName(u, activeSpecialty)
    }));
  const topicOptions = topics
    .filter(t => !filterUnit || t.unitId === filterUnit)
    .map(t => ({
      value: t.topicId,
      label: getDisplayName(t, activeSpecialty)
    }));
  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];
  const qualityOptions = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'simple', label: 'Simple' }
  ];

  // M10/M9: Use shared sanitized helper for all Draft.js rendering
  const renderQuestionText = (questionText) => {
    if (typeof questionText === 'string') return questionText;
    return draftRawToHtml(questionText);
  };

  const renderChoiceText = (choiceText) => {
    if (typeof choiceText === 'string') return choiceText;
    return draftRawToHtml(choiceText);
  };

  const renderExplanationText = (explanationText) => {
    if (typeof explanationText === 'string') return explanationText;
    return draftRawToHtml(explanationText);
  };

  const renderPassageText = (passageText) => draftRawToHtml(passageText);

  // Extract plain text from Draft.js content for search
  const extractPlainText = (content) => {
    try {
      if (!content) return '';
      if (typeof content === 'string') return content;
      if (content.blocks) {
        return content.blocks.map(b => b.text).join(' ');
      }
      return '';
    } catch (error) {
      return '';
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterSubject, filterUnit, filterTopic, filterDifficulty, filterQuality]);

  // Filter questions based on search and filters
  const filteredQuestions = useMemo(() => {
    return questions.filter(question => {
      // Apply subject filter
      if (filterSubject && question.subjectId !== filterSubject) return false;

      // Apply unit filter
      if (filterUnit && question.unitId !== filterUnit) return false;

      // Apply topic (subunit) filter
      if (filterTopic && question.topicId !== filterTopic) return false;

      // Apply difficulty filter
      if (filterDifficulty && question.difficulty !== filterDifficulty) return false;

      // Apply quality filter
      if (filterQuality && question.quality !== filterQuality) return false;

      // Apply search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();

        // Search by question ID
        if (question.questionId?.toLowerCase().includes(query)) return true;

        // Search by question code
        if (question.questionCode?.toLowerCase().includes(query)) return true;

        // Search in question text
        const questionText = extractPlainText(question.questionText).toLowerCase();
        if (questionText.includes(query)) return true;

        // Search in explanation
        const explanationText = extractPlainText(question.explanationText).toLowerCase();
        if (explanationText.includes(query)) return true;

        // Search in choices
        const choicesText = question.choices?.map(c =>
          typeof c.text === 'string' ? c.text : extractPlainText(c.text)
        ).join(' ').toLowerCase() || '';
        if (choicesText.includes(query)) return true;

        // Search in tags
        const tagsText = question.tags?.join(' ').toLowerCase() || '';
        if (tagsText.includes(query)) return true;

        // Search by subject name (with code)
        const subject = subjects.find(s => s.subjectId === question.subjectId);
        const subjectText = subject ? getDisplayName(subject, activeSpecialty).toLowerCase() : '';
        if (subjectText.includes(query)) return true;

        // Search by unit name (with code)
        const unit = units.find(u => u.unitId === question.unitId);
        const unitText = unit ? getDisplayName(unit, activeSpecialty).toLowerCase() : '';
        if (unitText.includes(query)) return true;

        // Search by topic name (with code)
        if (question.topicId) {
          const topic = topics.find(t => t.topicId === question.topicId);
          const topicText = topic ? getDisplayName(topic, activeSpecialty).toLowerCase() : '';
          if (topicText.includes(query)) return true;
        }

        return false;
      }

      return true;
    });
  }, [questions, subjects, units, topics, searchQuery, filterSubject, filterUnit, filterTopic, filterDifficulty, filterQuality]);

  // Get subject, unit, and topic names (with code prefix)
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.subjectId === subjectId);
    if (!subject) return 'Unknown';
    return getDisplayName(subject, activeSpecialty);
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    if (!unit) return 'Unknown';
    return getDisplayName(unit, activeSpecialty);
  };

  const getTopicName = (topicId) => {
    if (!topicId) return null;
    const topic = topics.find(t => t.topicId === topicId);
    if (!topic) return null;
    return getDisplayName(topic, activeSpecialty);
  };

  // Get passage for question
  const getPassage = (passageId) => {
    return passages.find(p => p.passageId === passageId);
  };

  // Handle question selection
  const handleSelectQuestion = (question, index) => {
    setSelectedQuestion(question);
    setCurrentQuestionIndex(index);
    setIsModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Navigate to previous/next question in filtered list
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedQuestion(filteredQuestions[newIndex]);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      setSelectedQuestion(filteredQuestions[newIndex]);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);
  const paginatedQuestions = filteredQuestions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setFilterSubject(null);
    setFilterUnit(null);
    setFilterTopic(null);
    setFilterDifficulty(null);
    setFilterQuality(null);
    setSelectedQuestion(null);
  };

  // Render difficulty badge
  const renderDifficultyBadge = (difficulty) => {
    const colors = {
      easy: 'success',
      medium: 'warning',
      hard: 'danger'
    };
    return (
      <Badge color={colors[difficulty] || 'secondary'} className="text-capitalize">
        {difficulty || 'N/A'}
      </Badge>
    );
  };

  return (
    <>
      <Header />
      <AccessRestriction>
      <div className="container-fluid mt--7">
        <TrialBanner />
        <BoardsCountdownBanner />
        <AddonCrossSellBanner />
        <Row>
          {/* Search and Filter Section */}
          <Col lg="12" className="mb-4">
            <Card className="shadow">
              <CardHeader className="bg-transparent border-0">
                <Row className="align-items-center">
                  <Col>
                    <h3 className="mb-0">Search Questions</h3>
                    <p className="text-muted small mb-0 mt-1">
                      Search by question ID, code, keyword, or filter by subject/unit
                    </p>
                  </Col>
                  {(searchQuery || filterSubject || filterUnit || filterTopic || filterQuality) && (
                    <Col xs="auto">
                      <Button color="link" size="sm" onClick={handleResetFilters}>
                        <i className="fas fa-times mr-1"></i>
                        Clear Filters
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                {/* Search Input */}
                <InputGroup className="mb-3">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-search"></i>
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Search by question ID, code, or keyword..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>

                {/* Filters */}
                <Row>
                  <Col md="3" className="mb-2">
                    <Select
                      placeholder="Filter by Subject"
                      options={subjectOptions}
                      value={subjectOptions.find(o => o.value === filterSubject) || null}
                      onChange={(opt) => {
                        setFilterSubject(opt?.value || null);
                        setFilterUnit(null);
                        setFilterTopic(null);
                      }}
                      isClearable
                      styles={customDropdownWithBorder}
                    />
                  </Col>
                  <Col md="3" className="mb-2">
                    <Select
                      placeholder="Filter by Unit"
                      options={unitOptions}
                      value={unitOptions.find(o => o.value === filterUnit) || null}
                      onChange={(opt) => {
                        setFilterUnit(opt?.value || null);
                        setFilterTopic(null);
                      }}
                      isClearable
                      isDisabled={!filterSubject}
                      styles={customDropdownWithBorder}
                    />
                  </Col>
                  <Col md="3" className="mb-2">
                    <Select
                      placeholder="Filter by Topic"
                      options={topicOptions}
                      value={topicOptions.find(o => o.value === filterTopic) || null}
                      onChange={(opt) => setFilterTopic(opt?.value || null)}
                      isClearable
                      isDisabled={!filterUnit}
                      styles={customDropdownWithBorder}
                    />
                  </Col>
                  {/* Quality filter hidden for now
                  <Col md="3" className="mb-2">
                    <Select
                      placeholder="Filter by Quality"
                      options={qualityOptions}
                      value={qualityOptions.find(o => o.value === filterQuality) || null}
                      onChange={(opt) => setFilterQuality(opt?.value || null)}
                      isClearable
                      styles={customDropdownWithBorder}
                    />
                  </Col>
                  */}
                  {/* Difficulty filter hidden for now */}
                </Row>

                {/* Results count */}
                <div className="text-muted small mb-3">
                  {isInitialLoading ? (
                    <span><Spinner size="sm" className="mr-2" />Loading questions...</span>
                  ) : (
                    <span>
                      Showing {((currentPage - 1) * questionsPerPage) + 1}–{Math.min(currentPage * questionsPerPage, filteredQuestions.length)} of {filteredQuestions.length} questions
                      {filteredQuestions.length !== questions.length && ` (${questions.length} total)`}
                    </span>
                  )}
                </div>

                {/* Questions List */}
                {isInitialLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    <p className="text-muted mt-3 mb-0">Loading question bank data...</p>
                  </div>
                ) : (
                  <>
                    <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                      {paginatedQuestions.length === 0 ? (
                        <div className="text-center py-4">
                          <i className="fas fa-search fa-2x text-muted mb-2"></i>
                          <p className="text-muted mb-0">No questions found</p>
                        </div>
                      ) : (
                        paginatedQuestions.map((question) => {
                          // Use the index in the full filtered list for modal navigation
                          const globalIndex = filteredQuestions.findIndex(q => q.questionId === question.questionId);
                          return (
                            <div
                              key={question.questionId}
                              className={`p-3 border rounded mb-2 cursor-pointer ${
                                selectedQuestion?.questionId === question.questionId
                                  ? 'border-primary bg-lighter'
                                  : 'hover-shadow'
                              }`}
                              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              onClick={() => handleSelectQuestion(question, globalIndex)}
                            >
                              <div className="d-flex flex-wrap mb-2">
                                {question.questionCode && (
                                  <Badge color="info" className="mr-2 mb-1">
                                    {question.questionCode}
                                  </Badge>
                                )}
                                <Badge color="primary" className="mr-2 mb-1">{getSubjectName(question.subjectId)}</Badge>
                                <Badge color="primary" className="mr-2 mb-1">{getUnitName(question.unitId)}</Badge>
                                {getTopicName(question.topicId) && (
                                  <Badge color="primary" className="mr-2 mb-1">
                                    {getTopicName(question.topicId)}
                                  </Badge>
                                )}
                                {/* Quality badge hidden for now
                                {question.quality && (
                                  <Badge color={question.quality === 'excellent' ? 'success' : 'info'} pill className="mr-2 mb-1 text-uppercase">
                                    {question.quality}
                                  </Badge>
                                )}
                                */}
                                {question.passageId ? (
                                  <Badge color="warning" pill className="mb-1">
                                    <i className="fas fa-file-alt mr-1"></i>
                                    Passage
                                  </Badge>
                                ) : (
                                  <Badge color="warning" pill className="mb-1">
                                    <i className="fas fa-question mr-1"></i>
                                    Standalone
                                  </Badge>
                                )}
                              </div>
                              <div
                                className="text-dark small"
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}
                              >
                                {extractPlainText(question.questionText).substring(0, 150)}
                                {extractPlainText(question.questionText).length > 150 && '...'}
                              </div>
                              <div className="text-muted small mt-2">
                                {question.createdAt && (
                                  <span className="mr-3">
                                    <i className="fas fa-calendar-plus mr-1"></i>
                                    Created: {formatDate(question.createdAt)}
                                  </span>
                                )}
                                {question.updatedAt && (
                                  <span>
                                    <i className="fas fa-calendar-check mr-1"></i>
                                    Updated: {formatDate(question.updatedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          Page {currentPage} of {totalPages}
                        </small>
                        <Pagination size="sm" className="mb-0">
                          <PaginationItem disabled={currentPage === 1}>
                            <PaginationLink first onClick={() => handlePageChange(1)} />
                          </PaginationItem>
                          <PaginationItem disabled={currentPage === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                          </PaginationItem>
                          {/* Show up to 5 page numbers centered around current page */}
                          {(() => {
                            let startPage = Math.max(1, currentPage - 2);
                            let endPage = Math.min(totalPages, startPage + 4);
                            if (endPage - startPage < 4) {
                              startPage = Math.max(1, endPage - 4);
                            }
                            const pages = [];
                            for (let i = startPage; i <= endPage; i++) {
                              pages.push(
                                <PaginationItem key={i} active={i === currentPage}>
                                  <PaginationLink onClick={() => handlePageChange(i)}>
                                    {i}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            }
                            return pages;
                          })()}
                          <PaginationItem disabled={currentPage === totalPages}>
                            <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                          </PaginationItem>
                          <PaginationItem disabled={currentPage === totalPages}>
                            <PaginationLink last onClick={() => handlePageChange(totalPages)} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

        </Row>
      </div>

      {/* Question View Modal */}
      <Modal isOpen={isModalOpen} toggle={handleCloseModal} size="xl" scrollable>
        <ModalHeader toggle={handleCloseModal}>
          {selectedQuestion && (
            <div className="d-flex align-items-center flex-wrap">
              <Badge color="info" className="mr-2" style={{ fontSize: '0.8rem' }}>
                {selectedQuestion.questionCode || selectedQuestion.questionId?.substring(0, 8)}
              </Badge>
              {/* {renderDifficultyBadge(selectedQuestion.difficulty)} */}
              <span className="ml-1 text-muted">
                {getSubjectName(selectedQuestion.subjectId)} / {getUnitName(selectedQuestion.unitId)}{getTopicName(selectedQuestion.topicId) ? ` / ${getTopicName(selectedQuestion.topicId)}` : ''}
              </span>
            </div>
          )}
        </ModalHeader>
        <ModalBody className="exam-content-font" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedQuestion && (
            <>
              {/* Passage (if passage-based) */}
              {selectedQuestion.passageId && (() => {
                const passage = getPassage(selectedQuestion.passageId);
                if (!passage) return null;
                return (
                  <div className="mb-4 p-3 rounded border">
                    <h6 className="text-muted mb-2">
                      <i className="fas fa-file-alt mr-2"></i>
                      Passage: {passage.title || 'Untitled'}
                    </h6>
                    <div
                      className="passage-content"
                      style={{ fontSize: '0.9rem', lineHeight: '1.6' }}
                      dangerouslySetInnerHTML={{ __html: renderPassageText(passage.passageText) }}
                    />
                  </div>
                );
              })()}

              {/* Question Text */}
              <div className="mb-4">
                <h6 className="text-primary mb-2">
                  <i className="fas fa-question-circle mr-2"></i>
                  Question
                </h6>
                <div
                  className="question-content"
                  style={{ fontSize: '1rem', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: renderQuestionText(selectedQuestion.questionText) }}
                />
              </div>

              {/* Answer Choices */}
              <div className="mb-4">
                <h6 className="text-primary mb-2">
                  <i className="fas fa-list-ul mr-2"></i>
                  Answer Choices
                </h6>
                {selectedQuestion.choices?.map((choice, index) => {
                  const isCorrect = selectedQuestion.correctAnswer?.includes(choice.choiceId);
                  const letter = String.fromCharCode(65 + index);
                  return (
                    <div
                      key={choice.choiceId}
                      className={`p-2 mb-2 rounded border ${
                        isCorrect ? 'border-success' : ''
                      }`}
                      style={{
                        backgroundColor: isCorrect ? 'rgba(40, 167, 69, 0.1)' : 'transparent'
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div
                          className={`rounded-circle d-flex align-items-center justify-content-center mr-2 ${
                            isCorrect ? 'bg-success text-white' : 'bg-light text-dark'
                          }`}
                          style={{ width: '28px', height: '28px', minWidth: '28px', fontSize: '0.85rem' }}
                        >
                          {isCorrect ? <i className="fas fa-check"></i> : letter}
                        </div>
                        <div
                          className="flex-grow-1"
                          style={{ fontSize: '0.95rem' }}
                          dangerouslySetInnerHTML={{
                            __html: typeof choice.text === 'string'
                              ? choice.text
                              : renderChoiceText(choice.text)
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Explanation */}
              {selectedQuestion.explanationText && (
                <div className="mb-4 p-3 border rounded">
                  <h6 className="mb-2" style={{ color: '#f57c00' }}>
                    <i className="fas fa-lightbulb mr-2"></i>
                    Explanation
                  </h6>
                  <div
                    className="explanation-content"
                    style={{ fontSize: '0.95rem', lineHeight: '1.6' }}
                    dangerouslySetInnerHTML={{ __html: renderExplanationText(selectedQuestion.explanationText) }}
                  />
                </div>
              )}

              {/* Question Metadata */}
              <div className="p-3 rounded border">
                <h6 className="text-muted mb-2">
                  <i className="fas fa-info-circle mr-2"></i>
                  Question Details
                </h6>
                <Row>
                  <Col xs="6" md="4" className="mb-2">
                    <small className="text-muted d-block">Subject</small>
                    <span className="small font-weight-bold">{getSubjectName(selectedQuestion.subjectId)}</span>
                  </Col>
                  <Col xs="6" md="4" className="mb-2">
                    <small className="text-muted d-block">Unit</small>
                    <span className="small font-weight-bold">{getUnitName(selectedQuestion.unitId)}</span>
                  </Col>
                  <Col xs="6" md="4" className="mb-2">
                    <small className="text-muted d-block">Topic</small>
                    <span className="small font-weight-bold">{getTopicName(selectedQuestion.topicId) || 'N/A'}</span>
                  </Col>
                  {/* Difficulty detail hidden for now */}
                  {/* Quality detail hidden for now
                  <Col xs="6" md="4" className="mb-2">
                    <small className="text-muted d-block">Quality</small>
                    <Badge color={selectedQuestion.quality === 'excellent' ? 'success' : selectedQuestion.quality === 'simple' ? 'info' : 'secondary'} className="text-capitalize">
                      {selectedQuestion.quality || 'N/A'}
                    </Badge>
                  </Col>
                  */}
                  <Col xs="6" md="4" className="mb-2">
                    <small className="text-muted d-block">Times Answered</small>
                    <span className="small">{selectedQuestion.timesAnswered || 0}</span>
                  </Col>
                  <Col xs="6" md="4" className="mb-2">
                    <small className="text-muted d-block">Correct %</small>
                    <span className="small">
                      {selectedQuestion.timesAnswered > 0
                        ? `${Math.round((selectedQuestion.timesCorrect / selectedQuestion.timesAnswered) * 100)}%`
                        : 'N/A'
                      }
                    </span>
                  </Col>
                  {selectedQuestion.tags?.length > 0 && (
                    <Col xs="12" className="mb-2">
                      <small className="text-muted d-block mb-1">Tags</small>
                      {selectedQuestion.tags.map((tag, i) => (
                        <Badge key={i} color="primary" className="mr-1">
                          {tag}
                        </Badge>
                      ))}
                    </Col>
                  )}
                </Row>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <Button
            color="primary"
            size="sm"
            disabled={currentQuestionIndex === 0}
            onClick={handlePrevQuestion}
          >
            <i className="fas fa-chevron-left mr-1"></i>
            Previous
          </Button>
          <span className="text-muted small">
            {currentQuestionIndex + 1} of {filteredQuestions.length}
          </span>
          <Button
            color="primary"
            size="sm"
            disabled={currentQuestionIndex >= filteredQuestions.length - 1}
            onClick={handleNextQuestion}
          >
            Next
            <i className="fas fa-chevron-right ml-1"></i>
          </Button>
        </ModalFooter>
      </Modal>
      </AccessRestriction>
    </>
  );
};

export default SearchQuestions;
